import { useMemo, useState } from 'react';
import { DownloadIcon, PrinterIcon } from 'lucide-react';

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type {
  AdminReportExportFormat,
  ReportCategory,
  ReportPaymentMethod,
  ReportSourceType,
} from '@mds/shared/types/reporting';

import { useSession } from '@/auth/use-session';
import BarRanking, { BarRankingTable } from '@/components/charts/bar-ranking';
import DonutChart from '@/components/charts/donut-chart';
import TrendLine from '@/components/charts/trend-line';
import CustomerPicker from '@/components/customers/customer-picker';
import PrintFrame from '@/components/reporting/print-frame';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import EmptyState from '@/components/ui/empty-state';
import { controlCls } from '@/components/ui/field';
import Pager from '@/components/ui/pager';
import Select from '@mds/shared/ui/select';
import StatCard from '@/components/ui/stat-card';
import { Cell, Row, Table } from '@/components/ui/table';
import { SegmentedToggle, Toolbar } from '@/components/ui/toolbar';
import {
  CATEGORY_OPTIONS,
  CUSTOM_RANGE,
  RANGES,
  byWeekday,
  deltaLabel,
  money,
  percentOf,
  pickSeries,
  previousRange,
  rangeLabel,
  rangeOf,
  rangeProblem,
  rank,
  toSlices,
  type DateRange,
} from '@/domain/analytics';
import useDebounced from '@mds/shared/hooks/use-debounced';
import { dateStamp, stamp } from '@mds/shared/lib/dates';
import { printReport } from '@/lib/export';
import { formatCents } from '@/lib/money';
import * as customersApi from '@/customers/customers.api';
import * as reporting from '@/reporting/reporting.api';
import * as resources from '@/resources/resources.api';

type View = 'charts' | 'table';

const ALL = 'all';
const TRANSACTIONS_PER_PAGE = 25;

const METHODS: ReportPaymentMethod[] = ['cash', 'whish', 'omt', 'other'];

const SOURCES: { value: ReportSourceType | typeof ALL; label: string }[] = [
  { value: ALL, label: 'Bookings and events' },
  { value: 'booking', label: 'Bookings only' },
  { value: 'event_registration', label: 'Events only' },
];

const TRANSACTION_SORTS = [
  { value: 'recordedAt:desc', label: 'Most recent' },
  { value: 'recordedAt:asc', label: 'Oldest first' },
  { value: 'amountMinor:desc', label: 'Largest amount' },
  { value: 'customerName:asc', label: 'Customer A–Z' },
];

const plural = (n: number, one: string, many = one + 's') =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * What sells and what earns, over a window of money actually collected.
 *
 * One range and one set of filters drive every chart, the ledger and the
 * export, so all of them always describe the same window. The previous window
 * of the same length is read alongside, so each headline figure can say how it
 * moved. The finer series, the receivables figures and the extra rollups are
 * optional on the contract, so each card draws only when its data is present —
 * see `backend/docs/reporting.md`.
 */
export default function AnalyticsPage() {
  const { can } = useSession();
  const mayRead = can('reports.read');
  const mayExport = can('reports.export');
  const maySeeCustomers = can('crm.customers.read');

  const [rangeChoice, setRangeChoice] = useState(String(RANGES[3].days));
  const [custom, setCustom] = useState<DateRange>(() => rangeOf(RANGES[0].days));
  const [category, setCategory] = useState(ALL);
  const [resourceId, setResourceId] = useState(ALL);
  const [customerId, setCustomerId] = useState('');
  const [view, setView] = useState<View>('charts');
  const [exporting, setExporting] = useState<AdminReportExportFormat | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [method, setMethod] = useState(ALL);
  const [source, setSource] = useState(ALL);
  const [sort, setSort] = useState('recordedAt:desc');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim());

  const preset = RANGES.find((r) => String(r.days) === rangeChoice);
  const customProblem = preset ? null : rangeProblem(custom);
  // Null while a custom range is half-typed, which pauses every query below.
  const range = useMemo<DateRange | null>(
    () => (preset ? rangeOf(preset.days) : customProblem ? null : { ...custom }),
    [preset, custom, customProblem],
  );

  const filters = useMemo(
    () =>
      range
        ? {
            ...range,
            ...(category === ALL ? {} : { category: category as ReportCategory }),
            ...(resourceId === ALL ? {} : { resourceId }),
            ...(customerId ? { customerId } : {}),
          }
        : null,
    [range, category, resourceId, customerId],
  );

  const resetPage = () => setPage(1);

  const spaces = useQuery({
    queryKey: ['resources', { includeInactive: true }],
    queryFn: () => resources.listResources({ includeInactive: true }),
    enabled: mayRead,
    staleTime: 5 * 60_000,
  });

  // The chosen customer by name, so the printed page says who, as the
  // workbook's title block does.
  const customer = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customersApi.getCustomer(customerId),
    enabled: mayRead && customerId !== '',
    staleTime: 5 * 60_000,
  });

  const analytics = useQuery({
    queryKey: ['reports', 'analytics', filters],
    queryFn: () => reporting.getAnalytics(filters as NonNullable<typeof filters>),
    enabled: mayRead && filters !== null,
  });

  // The same filters over the window just before this one, for the deltas.
  const previous = useQuery({
    queryKey: ['reports', 'analytics', 'previous', filters],
    queryFn: () =>
      reporting.getAnalytics({
        ...(filters as NonNullable<typeof filters>),
        ...previousRange(range as DateRange),
      }),
    enabled: mayRead && filters !== null,
  });

  const [sortKey, direction] = sort.split(':') as [
    'recordedAt' | 'amountMinor' | 'customerName',
    'asc' | 'desc',
  ];

  const transactions = useQuery({
    queryKey: ['reports', 'transactions', filters, page, method, source, sort, q],
    queryFn: () =>
      reporting.listTransactions({
        ...(filters as NonNullable<typeof filters>),
        page,
        limit: TRANSACTIONS_PER_PAGE,
        ...(method === ALL ? {} : { method: method as ReportPaymentMethod }),
        ...(source === ALL ? {} : { sourceType: source as ReportSourceType }),
        ...(q ? { search: q } : {}),
        sort: sortKey,
        direction,
      }),
    enabled: mayRead && filters !== null,
  });

  const data = analytics.data;
  const before = previous.data?.summary;

  const spacesByRevenue = useMemo(() => rank(data?.bySpace ?? [], 'revenue'), [data]);
  const spacesBySales = useMemo(() => rank(data?.bySpace ?? [], 'sales'), [data]);
  const packagesByRevenue = useMemo(() => rank(data?.byPackage ?? [], 'revenue'), [data]);
  const packagesBySales = useMemo(() => rank(data?.byPackage ?? [], 'sales'), [data]);
  const eventsByRevenue = useMemo(() => rank(data?.byEvent ?? [], 'revenue'), [data]);
  const mix = useMemo(() => rank(data?.byCategory ?? [], 'revenue'), [data]);
  const methods = useMemo(() => rank(data?.byMethod ?? [], 'revenue'), [data]);
  const customers = useMemo(() => rank(data?.byCustomer ?? [], 'revenue'), [data]);
  const weekdays = useMemo(() => byWeekday(data?.byWeekday ?? [], 'revenue'), [data]);

  // Every bucket in the range comes back, including the empty ones, so the line
  // never closes a gap that was really a quiet stretch.
  const trend = useMemo(
    () => (data && range ? pickSeries(data, range) : { granularity: 'month' as const, points: [] }),
    [data, range],
  );

  /** The space rollup is keyed by resource slug; the filter wants the id. */
  const spaceIdBySlug = useMemo(
    () => new Map((spaces.data ?? []).map((s) => [s.slug, s.id])),
    [spaces.data],
  );

  const summary = data?.summary;
  const salesDelta = summary ? deltaLabel(summary.saleCount, before?.saleCount) : null;
  const receivables = data?.receivables;
  const cancellations = data?.cancellations;
  const customerSplit = data?.customers;
  const topSpaceRevenue = spacesByRevenue[0];
  const topSpaceVolume = spacesBySales[0];
  const rangeText = preset ? preset.label.toLowerCase() : range ? rangeLabel(range) : 'this range';
  const filtered = category !== ALL || resourceId !== ALL || customerId !== '';

  // What the printed page says about itself, by name rather than by id.
  const printSubtitle = range ? rangeLabel(range) : '';
  const printLines = useMemo(() => {
    const narrowed = [
      category === ALL ? null : CATEGORY_OPTIONS.find((c) => c.value === category)?.label,
      resourceId === ALL ? null : spaces.data?.find((s) => s.id === resourceId)?.name,
      customerId ? (customer.data?.name ?? 'One customer') : null,
    ].filter((v): v is string => Boolean(v));
    return [
      narrowed.length > 0 ? `Filtered to ${narrowed.join(' · ')}` : 'All activity',
      `Generated ${stamp(new Date().toISOString())}`,
    ];
  }, [category, resourceId, customerId, spaces.data, customer.data]);

  const runExport = async (format: AdminReportExportFormat) => {
    if (!filters) return;
    setExporting(format);
    setExportError(null);
    try {
      // The server names the file and counts the rows; neither is guessed here.
      const { blob, filename } = await reporting.exportAnalytics(filters, format);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setExportError(
        error instanceof ApiError
          ? error.message
          : 'The export could not be generated. Try a narrower range.',
      );
    } finally {
      setExporting(null);
    }
  };

  if (!mayRead) {
    return (
      <EmptyState
        title="Reporting is not available to your account"
        hint="Ask a super administrator for permission to read reports."
      />
    );
  }

  return (
    <PrintFrame title="Analytics report" subtitle={printSubtitle} lines={printLines}>
      <div className="flex flex-col gap-6">
        {/* One filter row above everything it scopes — never per-card filters. */}
        <div className="no-print flex flex-col gap-4 rounded-2xl border border-hairline bg-surface px-4 py-4 sm:px-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="text-base text-ink-secondary">
              {!range
                ? 'Pick a range to read the ledger.'
                : analytics.isPending
                  ? 'Reading the ledger…'
                  : summary
                    ? `${plural(summary.saleCount, 'sale')} · ${formatCents(
                        summary.collectedAmountMinor,
                      )} collected over the ${rangeText}${filtered ? ', filtered' : ''}.`
                    : 'Nothing collected in this range.'}
            </span>
            <Toolbar>
              <Select
                value={rangeChoice}
                onChange={(next) => {
                  // A custom range starts from the preset it replaces, not from blank.
                  if (next === CUSTOM_RANGE && preset) setCustom(rangeOf(preset.days));
                  setRangeChoice(next);
                  resetPage();
                }}
                options={[
                  ...RANGES.map((r) => ({ value: String(r.days), label: r.label })),
                  { value: CUSTOM_RANGE, label: 'Custom range' },
                ]}
                aria-label="Date range"
              />
              <SegmentedToggle
                value={view}
                options={['charts', 'table'] as const}
                onChange={setView}
              />
              {mayExport && (
                <>
                  <Button
                    variant="quiet"
                    disabled={exporting !== null || !filters}
                    onClick={() => runExport('csv')}
                  >
                    <DownloadIcon className="size-5" aria-hidden="true" />
                    {exporting === 'csv' ? 'Preparing…' : 'CSV'}
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={exporting !== null || !filters}
                    onClick={() => runExport('xlsx')}
                  >
                    <DownloadIcon className="size-5" aria-hidden="true" />
                    {exporting === 'xlsx' ? 'Preparing…' : 'Excel'}
                  </Button>
                </>
              )}
              <Button
                variant="quiet"
                disabled={!range}
                onClick={() =>
                  printReport(`MDS Analytics ${range?.from ?? ''} to ${range?.to ?? ''}`)
                }
              >
                <PrinterIcon className="size-5" aria-hidden="true" />
                PDF
              </Button>
            </Toolbar>
          </div>

          <div className="flex flex-wrap items-center gap-2 max-sm:*:min-w-[calc(50%-0.25rem)] max-sm:*:flex-1">
            {!preset && (
              <>
                <input
                  type="date"
                  value={custom.from}
                  max={custom.to}
                  onChange={(e) => {
                    setCustom((c) => ({ ...c, from: e.target.value }));
                    resetPage();
                  }}
                  aria-label="From date"
                  className={controlCls}
                />
                <span className="text-base text-ink-muted">to</span>
                <input
                  type="date"
                  value={custom.to}
                  min={custom.from}
                  onChange={(e) => {
                    setCustom((c) => ({ ...c, to: e.target.value }));
                    resetPage();
                  }}
                  aria-label="To date"
                  className={controlCls}
                />
              </>
            )}
            <Select
              value={category}
              onChange={(next) => {
                setCategory(next);
                resetPage();
              }}
              options={[{ value: ALL, label: 'All categories' }, ...CATEGORY_OPTIONS]}
              aria-label="Filter by category"
            />
            <Select
              value={resourceId}
              onChange={(next) => {
                setResourceId(next);
                resetPage();
              }}
              options={[
                { value: ALL, label: 'All spaces' },
                ...(spaces.data ?? []).map((s) => ({
                  value: s.id,
                  label: s.isActive ? s.name : `${s.name} (inactive)`,
                })),
              ]}
              aria-label="Filter by space"
            />
            {maySeeCustomers && (
              <CustomerPicker
                value={customerId}
                onChange={(next) => {
                  setCustomerId(next);
                  resetPage();
                }}
                anyLabel="Any customer"
                placeholder="Any customer"
                aria-label="Filter by customer"
                className="w-60"
              />
            )}
            {filtered && (
              <Button
                variant="quiet"
                onClick={() => {
                  setCategory(ALL);
                  setResourceId(ALL);
                  setCustomerId('');
                  resetPage();
                }}
              >
                Clear filters
              </Button>
            )}
            {customProblem && <span className="text-base text-warn-text">{customProblem}</span>}
          </div>
        </div>

        {exportError && <p className="m-0 text-base text-warn-text">{exportError}</p>}

        {analytics.isError ? (
          <EmptyState
            title="Could not load the analytics"
            hint={
              analytics.error instanceof ApiError
                ? analytics.error.message
                : 'The server could not be reached.'
            }
          />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5 print:grid-cols-5">
              <StatCard
                label="Collected"
                value={summary ? formatCents(summary.collectedAmountMinor) : '—'}
                note={
                  summary
                    ? [
                        `${plural(summary.paymentCount, 'payment')} across ${rangeText}`,
                        deltaLabel(summary.collectedAmountMinor, before?.collectedAmountMinor),
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : `Across ${rangeText}`
                }
                size="hero"
              />
              <StatCard
                label="Top earner"
                size="sm"
                value={topSpaceRevenue?.label ?? '—'}
                note={
                  topSpaceRevenue
                    ? `${money(topSpaceRevenue.collectedAmountMinor ?? 0)} · ${plural(
                        topSpaceRevenue.sales ?? 0,
                        'sale',
                      )}`
                    : '—'
                }
              />
              <StatCard
                label="Expenses"
                size="sm"
                value={summary ? formatCents(summary.expenseAmountMinor) : '—'}
                note={
                  summary
                    ? `${plural(summary.expenseCount, 'expense')} · ${formatCents(summary.netAmountMinor)} net`
                    : '—'
                }
              />
              <StatCard
                label="Most booked"
                size="sm"
                value={topSpaceVolume?.label ?? '—'}
                note={
                  topSpaceVolume
                    ? `${plural(topSpaceVolume.value, 'sale')} · ${money(
                        topSpaceVolume.collectedAmountMinor ?? 0,
                      )}`
                    : '—'
                }
              />
              <StatCard
                label="Average sale"
                size="sm"
                value={summary ? formatCents(summary.averageSaleAmountMinor) : '—'}
                note={
                  summary
                    ? [
                        `${plural(summary.saleCount, 'sale')} · ${plural(
                          summary.bookingCount,
                          'booking',
                        )} · ${plural(summary.eventRegistrationCount, 'registration')}`,
                        salesDelta && `sales ${salesDelta}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : '—'
                }
              />
            </div>

            {/* Billed against collected is a different grain from the ledger, so
              these tiles wait for the server's receivables block. */}
            {(receivables || cancellations || customerSplit) && (
              <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4 print:grid-cols-4">
                {receivables && (
                  <>
                    <StatCard
                      label="Billed"
                      size="sm"
                      value={formatCents(receivables.billedAmountMinor)}
                      note={`${percentOf(receivables.collectionRateBasisPoints)} collected so far`}
                      density="compact"
                    />
                    <StatCard
                      label="Still owed"
                      size="sm"
                      value={formatCents(receivables.outstandingAmountMinor)}
                      note={`${plural(receivables.unpaidBookingCount, 'sale')} unpaid · ${plural(
                        receivables.depositOnlyBookingCount,
                        'sale',
                      )} on deposit`}
                      density="compact"
                    />
                  </>
                )}
                {cancellations && (
                  <StatCard
                    label="Cancelled"
                    size="sm"
                    value={String(
                      cancellations.cancelledBookingCount +
                        cancellations.cancelledRegistrationCount,
                    )}
                    note={`${formatCents(cancellations.retainedDepositAmountMinor)} in deposits kept`}
                    density="compact"
                  />
                )}
                {customerSplit && (
                  <StatCard
                    label="New customers"
                    size="sm"
                    value={String(customerSplit.newCustomerCount)}
                    note={`${plural(customerSplit.returningCustomerCount, 'returning customer')}`}
                    density="compact"
                  />
                )}
              </div>
            )}

            {/* Revenue and volume are different measures, so each gets its own
              chart on its own scale rather than one plot with two axes. The
              donut answers "what share"; the bar beside it answers "how much". */}
            <div className="grid gap-6 xl:grid-cols-2 print:grid-cols-2">
              <Card title="Revenue mix">
                {view === 'charts' ? (
                  <DonutChart
                    slices={toSlices(mix)}
                    total={summary ? money(summary.collectedAmountMinor) : '—'}
                    totalLabel="all revenue"
                  />
                ) : (
                  <BarRankingTable rows={mix} measure="Collected" />
                )}
              </Card>

              <Card title="Revenue by space">
                {view === 'charts' ? (
                  <BarRanking
                    rows={spacesByRevenue}
                    // A click narrows the whole page to that space.
                    onSelect={
                      resourceId === ALL
                        ? (row) => {
                            const id = spaceIdBySlug.get(row.key);
                            if (id) {
                              setResourceId(id);
                              resetPage();
                            }
                          }
                        : undefined
                    }
                  />
                ) : (
                  <BarRankingTable rows={spacesByRevenue} measure="Collected" />
                )}
              </Card>

              <Card title="Sales by space">
                {view === 'charts' ? (
                  <BarRanking rows={spacesBySales} />
                ) : (
                  <BarRankingTable rows={spacesBySales} measure="Sales" />
                )}
              </Card>

              <Card title="Revenue by package">
                {view === 'charts' ? (
                  <BarRanking rows={packagesByRevenue} />
                ) : (
                  <BarRankingTable rows={packagesByRevenue} measure="Collected" />
                )}
              </Card>

              <Card title="Packages sold">
                {view === 'charts' ? (
                  <BarRanking rows={packagesBySales} />
                ) : (
                  <BarRankingTable rows={packagesBySales} measure="Sold" />
                )}
              </Card>

              <Card title="Revenue by event">
                {view === 'charts' ? (
                  <BarRanking rows={eventsByRevenue} emptyHint="No event income in this range." />
                ) : (
                  <BarRankingTable rows={eventsByRevenue} measure="Collected" />
                )}
              </Card>

              {data?.byMethod && (
                <Card title="Payment methods">
                  {view === 'charts' ? (
                    <DonutChart
                      slices={toSlices(methods)}
                      total={summary ? money(summary.collectedAmountMinor) : '—'}
                      totalLabel="all payments"
                    />
                  ) : (
                    <BarRankingTable rows={methods} measure="Collected" />
                  )}
                </Card>
              )}

              {data?.byCustomer && (
                <Card title="Top customers">
                  {view === 'charts' ? (
                    <BarRanking
                      rows={customers.slice(0, 10)}
                      emptyHint="No customer income in this range."
                    />
                  ) : (
                    <BarRankingTable rows={customers} measure="Collected" />
                  )}
                </Card>
              )}

              {data?.byWeekday && (
                <Card title="Revenue by weekday">
                  {view === 'charts' ? (
                    <BarRanking rows={weekdays} />
                  ) : (
                    <BarRankingTable rows={weekdays} measure="Collected" />
                  )}
                </Card>
              )}
            </div>

            <Card title={`Revenue by ${trend.granularity}`}>
              {view === 'charts' ? (
                <TrendLine points={trend.points} format={money} granularity={trend.granularity} />
              ) : (
                <BarRankingTable
                  rows={trend.points.map((p) => ({
                    key: p.key,
                    label: p.key + (p.partial ? ' (to date)' : ''),
                    value: p.value,
                    display: money(p.value),
                  }))}
                  measure="Collected"
                />
              )}
            </Card>

            <Card
              title="Transactions"
              action={
                <Toolbar className="no-print">
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      resetPage();
                    }}
                    placeholder="Search transactions"
                    aria-label="Search transactions"
                    maxLength={160}
                    className={controlCls + ' w-[260px]'}
                  />
                  <Select
                    value={source}
                    onChange={(next) => {
                      setSource(next);
                      resetPage();
                    }}
                    options={SOURCES}
                    aria-label="Filter by source"
                  />
                  <Select
                    value={method}
                    onChange={(next) => {
                      setMethod(next);
                      resetPage();
                    }}
                    options={[
                      { value: ALL, label: 'All methods' },
                      ...METHODS.map((m) => ({ value: m, label: m })),
                    ]}
                    aria-label="Filter by method"
                  />
                  <Select
                    value={sort}
                    onChange={(next) => {
                      setSort(next);
                      resetPage();
                    }}
                    options={TRANSACTION_SORTS}
                    aria-label="Sort transactions"
                  />
                </Toolbar>
              }
            >
              {!filters || transactions.isPending ? (
                <p className="m-0 text-base text-ink-muted">Reading the ledger…</p>
              ) : transactions.isError ? (
                <EmptyState
                  title="Could not load the transactions"
                  hint={
                    transactions.error instanceof ApiError
                      ? transactions.error.message
                      : 'The server could not be reached.'
                  }
                />
              ) : (transactions.data?.data.length ?? 0) === 0 ? (
                <EmptyState
                  title="No transactions match"
                  hint="Nothing was collected in this range with these filters."
                />
              ) : (
                <>
                  {/* The summary covers the whole filtered result, not this page —
                    a total summed from 25 rows would be wrong and look right. */}
                  <p className="mt-0 mb-4 text-base text-ink-secondary">
                    {plural(transactions.data?.pagination.total ?? 0, 'transaction')} ·{' '}
                    {formatCents(transactions.data?.summary.collectedAmountMinor ?? 0)} collected.
                  </p>

                  <Table
                    head={[
                      'Recorded',
                      'Reference',
                      'Customer',
                      'Item',
                      'Source',
                      'Method',
                      'Amount',
                    ]}
                  >
                    {(transactions.data?.data ?? []).map((t) => (
                      <Row key={t.id}>
                        <Cell className="font-mono text-md text-ink-muted">{dateStamp(t.recordedDate)}</Cell>
                        <Cell className="font-mono text-md">{t.reference}</Cell>
                        <Cell>
                          <span className="block">{t.customer.name}</span>
                          {t.customer.email && (
                            <span className="block text-md text-ink-muted">{t.customer.email}</span>
                          )}
                        </Cell>
                        <Cell>
                          <span className="block">{t.item.name}</span>
                          {t.resource && (
                            <span className="block text-md text-ink-muted">{t.resource.name}</span>
                          )}
                        </Cell>
                        <Cell className="font-mono text-md">{t.sourceReference}</Cell>
                        <Cell>{t.methodLabel}</Cell>
                        <Cell className="font-mono text-md">{formatCents(t.amountMinor)}</Cell>
                      </Row>
                    ))}
                  </Table>

                  <Pager pagination={transactions.data?.pagination} onPage={setPage} />
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </PrintFrame>
  );
}
